// 覚醒王の回転 feralPocketSpin（5枚版）の付随演出。見本 = C:\dev\LUNAIRE-notes\assets\2026-10-02-awoken-spin-mock\spin_mock.py
//   尾の弧の残像（奥と手前に分ける）・床の土煙・直前の絵の残像・着地の揺れ。顔の発光は足さない。
//   🔴 状態を持たない: 全て「回転の頭からの経過 ms」と乱数の添字だけで決まる = ラボの一時停止・巻き戻しでも同じ見え方。
//   時間割（ms・頭から）: wind 0-430 / rear 430-500 / sweep 500-600（当たり 520）/ front 600-670 / settle 670-1000。
import type { Boss } from './types'
import { AWOKEN_SPIN_FRONT_PAW_OFFSET_PX } from './awoken_pocket_spin'

export const AWOKEN_SPIN_ID = 'feralPocketSpin'
/** 見本の描画基準（待機の高さ 470px）。見本の px 値は全部この比で本番へ写す。 */
const MOCK_REF_H = 470
const MOCK_R_X = 656
const MOCK_R_Y = MOCK_R_X * 0.2
export const AWOKEN_SPIN_GHOST_ALPHA = 0.22
export const AWOKEN_SPIN_GHOST_FROM_MS = 430
export const AWOKEN_SPIN_GHOST_TO_MS = 760
export const AWOKEN_SPIN_SHAKE_FROM_MS = 820
export const AWOKEN_SPIN_SHAKE_TO_MS = 900
const FX_END_FADE_MS = 130
// 見本は暗い無地の背景。本番の床は明るく灰紫なので、同じ色・濃さだと土煙も弧もほぼ見えない（10-02 実測）→ 濃さを足す・土煙は明るい灰へ。
const ARC_GAIN = 1.9
const DUST_GAIN = 2.2
const DUST_RGB = '156,146,156'

const GHOST_PREV: Record<string, string> = {
  pocket_spin_rear_1002: 'pocket_spin_wind_1001',
  pocket_spin_sweep_1001: 'pocket_spin_rear_1002',
  pocket_spin_front_1002: 'pocket_spin_sweep_1001',
  pocket_spin_settle_1001: 'pocket_spin_front_1002',
  pocket_spin_settle_1002: 'pocket_spin_front_1002',
}

const hash01 = (n: number): number => { const s = Math.sin(n * 127.1 + 311.7) * 43758.5453; return s - Math.floor(s) }
const lerp = (a: number, b: number, u: number): number => a + (b - a) * u

/** 回転の頭からの経過 ms。回転中でなければ null。 */
export function awokenSpinMs(boss: Boss): number | null {
  const atk = boss.currentAttack
  if (boss.id !== 'crowned-king-awoken' || !atk || atk.id !== AWOKEN_SPIN_ID || boss.hp <= 0) return null
  if (boss.state === 'telegraph') return boss.stateElapsed * 1000
  if (boss.state === 'active') return atk.chargeMs + boss.stateElapsed * 1000
  if (boss.state === 'recovery') return atk.chargeMs + atk.activeMs + boss.stateElapsed * 1000
  return null
}

/** 技の終わり（recovery の終端）の ms。 */
function spinEndMs(boss: Boss): number {
  const atk = boss.currentAttack!
  return atk.chargeMs + atk.activeMs + atk.recoveryMs
}

/** 直前の絵を薄く重ねる残像（見本: 430〜760ms・直前の1枚・透明度0.22）。 */
export function awokenSpinGhostKey(drawKey: string, ms: number): string | null {
  if (ms < AWOKEN_SPIN_GHOST_FROM_MS || ms >= AWOKEN_SPIN_GHOST_TO_MS) return null
  return GHOST_PREV[drawKey] ?? null
}

/** 着地の揺れ（見本: 820〜900ms に ±3px・描画 470px 基準）。体の絵だけを横へずらす。 */
export function awokenSpinShakePx(ms: number, displayHeight: number): number {
  if (ms < AWOKEN_SPIN_SHAKE_FROM_MS || ms >= AWOKEN_SPIN_SHAKE_TO_MS) return 0
  return 3 * Math.sin(ms) * displayHeight / MOCK_REF_H
}

/**
 * 尾先の角度（度）。0 = 後ろ（画面の尾側）・90 = 手前（カメラ側）・180 = 前（相手側）・270 = 奥。
 * 溜めで少し奥へ引き（-12°）→ 一周 → 360° で元の後ろへ戻る。
 */
export function awokenSpinTailAngle(ms: number): number {
  if (ms < 330) return -12 * (ms / 330)
  if (ms < 670) return -12 + 312 * ((ms - 330) / 340)
  if (ms < 820) { const u = (ms - 670) / 150; return 300 + 60 * (1 - (1 - u) ** 3) }
  return 360
}

type Geo = { k: number; pivotX: number; floorY: number; back: number; rx: number; ry: number }
function geometry(boss: Boss, displayHeight: number, footY: number): Geo {
  const k = displayHeight / MOCK_REF_H
  const dir = boss.facing === 'right' ? 1 : -1
  return {
    k, back: -dir,
    // 前脚の接地点 = 回転の軸。向きが変わる瞬間も boss.x 側で同じ世界座標に保たれている（boss.ts の向き直り）。
    pivotX: boss.x + dir * AWOKEN_SPIN_FRONT_PAW_OFFSET_PX,
    // 絵の bbox 下端 = footY + 12。見本は床線の 4px 下に絵の下端を置く。
    floorY: footY + 12 - 4 * k,
    rx: MOCK_R_X * k, ry: MOCK_R_Y * k,
  }
}
const tipOf = (g: Geo, th: number): [number, number] => {
  const a = th * Math.PI / 180
  return [g.pivotX + g.back * g.rx * Math.cos(a), g.floorY - 6 * g.k + g.ry * Math.sin(a)]
}

// ---- 柔らかい層（見本の GaussianBlur 相当）: 小さな別キャンバスへ描いてからぼかして本画面へ ----
const LAYER_SCALE = 2
let layer: HTMLCanvasElement | null = null
function layerCanvas(w: number, h: number): HTMLCanvasElement | null {
  if (typeof document === 'undefined') return null
  if (!layer) layer = document.createElement('canvas')
  const pw = Math.ceil(w * LAYER_SCALE), ph = Math.ceil(h * LAYER_SCALE)
  if (layer.width !== pw || layer.height !== ph) { layer.width = pw; layer.height = ph }
  return layer
}
function drawSoft(ctx: CanvasRenderingContext2D, g: Geo, blurMockPx: number,
  paint: (lc: CanvasRenderingContext2D) => void): void {
  const w = 2 * g.rx + 700 * g.k, h = 160
  const cv = layerCanvas(w, h)
  const lc = cv?.getContext('2d')
  if (!cv || !lc) return
  const ox = g.pivotX - w / 2, oy = g.floorY - 90
  lc.setTransform(1, 0, 0, 1, 0, 0)
  lc.clearRect(0, 0, cv.width, cv.height)
  lc.setTransform(LAYER_SCALE, 0, 0, LAYER_SCALE, -ox * LAYER_SCALE, -oy * LAYER_SCALE)
  paint(lc)
  ctx.save()
  ctx.filter = `blur(${(blurMockPx * g.k).toFixed(2)}px)`
  ctx.drawImage(cv, ox, oy, w, h)
  ctx.restore()
}

function endFade(boss: Boss, ms: number): number {
  return Math.max(0, Math.min(1, (spinEndMs(boss) - ms) / FX_END_FADE_MS))
}

/** 尾先が空気を掃いた帯。half='back' は奥側の半周（体の後ろ・先に描く）、'front' は手前側（体の上）。 */
export function drawAwokenSpinArc(ctx: CanvasRenderingContext2D, boss: Boss,
  displayHeight: number, footY: number, half: 'back' | 'front'): void {
  const ms = awokenSpinMs(boss)
  if (ms === null || ms < 400) return
  const g = geometry(boss, displayHeight, footY)
  const th = awokenSpinTailAngle(ms)
  const fade = (ms < 700 ? 1 : Math.max(0, 1 - (ms - 700) / 260)) * endFade(boss, ms)
  if (fade <= 0) return
  const span = 170, steps = 80
  drawSoft(ctx, g, 5, lc => {
    for (let i = 0; i < steps; i++) {
      const w0 = i / steps, w1 = (i + 1) / steps
      const a0 = th - span + span * w0, a1 = th - span + span * w1
      if (a1 < -12) continue
      const mid = (a0 + a1) / 2
      if ((Math.sin(mid * Math.PI / 180) > 0) !== (half === 'front')) continue
      const [x0, y0] = tipOf(g, a0), [x1, y1] = tipOf(g, a1)
      const lead = Math.min(1, (1 - w1) / 0.08 + 0.15)
      const thick = (4 + 34 * w1 ** 1.3) * lead * g.k
      const al = Math.min(255, 150 * w1 ** 2 * fade * ARC_GAIN)
      lc.fillStyle = `rgba(170,155,205,${(al * 0.6 / 255).toFixed(3)})`
      lc.beginPath()
      lc.moveTo(x0, y0 - thick); lc.lineTo(x1, y1 - thick); lc.lineTo(x1, y1 + 2 * g.k); lc.lineTo(x0, y0 + 2 * g.k)
      lc.closePath(); lc.fill()
      lc.strokeStyle = `rgba(240,232,255,${(al * 0.9 / 255).toFixed(3)})`
      lc.lineWidth = Math.max(0.6, (1 + 3 * w1) * g.k)
      lc.beginPath(); lc.moveTo(x0, y0 - thick * 0.6); lc.lineTo(x1, y1 - thick * 0.6); lc.stroke()
    }
  })
}

type Puff = { te: number; life: number; size: number; kind: 'tail' | 'ring'; i: number; n: number; speed: number }
/** 土煙の一覧（出る時刻・寿命・大きさだけ。位置は描く時に尾先・軸から決める）。 */
const PUFFS: Puff[] = (() => {
  const out: Puff[] = []
  let id = 0
  for (let te = 440; te <= 720; te += 20) {
    for (let j = 0; j < 2; j++, id++) out.push({ te, kind: 'tail', i: id, n: 0, speed: 0,
      size: lerp(8, 26, hash01(id * 7 + 1)), life: lerp(450, 750, hash01(id * 7 + 2)) })
  }
  const ring = (te: number, n: number, speed: number): void => {
    for (let i = 0; i < n; i++, id++) out.push({ te, kind: 'ring', i, n, speed,
      size: lerp(10, 30, hash01(id * 7 + 3)), life: lerp(400, 700, hash01(id * 7 + 4)) })
  }
  ring(520, 22, 320)   // 当たり
  ring(820, 14, 200)   // 着地
  return out
})()
const DRAG_X = 3.63   // 見本: 20ms ごとに ×0.93
const DRAG_Y = 5.27   // 見本: 20ms ごとに ×0.9（+2px/s で落ちる）

export function drawAwokenSpinDust(ctx: CanvasRenderingContext2D, boss: Boss,
  displayHeight: number, footY: number): void {
  const ms = awokenSpinMs(boss)
  if (ms === null || ms < 430) return
  const g = geometry(boss, displayHeight, footY)
  const fadeAll = endFade(boss, ms)
  if (fadeAll <= 0) return
  const k = g.k
  drawSoft(ctx, g, 6, lc => {
    PUFFS.forEach((p, idx) => {
      const age = ms - p.te
      if (age < 0 || age >= p.life) return
      const h = (n: number): number => hash01(idx * 13 + n)
      let x0: number, y0: number, vx: number, vy: number
      if (p.kind === 'tail') {
        const [tx, ty] = tipOf(g, awokenSpinTailAngle(p.te))
        x0 = tx + lerp(-30, 30, h(1)) * k; y0 = ty + lerp(-4, 4, h(2)) * k
        vx = lerp(-60, 60, h(3)) * k; vy = lerp(-90, -30, h(4)) * k
      } else {
        const a = 2 * Math.PI * p.i / p.n + lerp(-0.15, 0.15, h(5))
        const v = p.speed * lerp(0.6, 1.2, h(6))
        x0 = g.pivotX + 30 * Math.cos(a) * k; y0 = g.floorY - 2 * k
        vx = v * Math.cos(a) * k; vy = (v * 0.22 * Math.sin(a) - lerp(10, 50, h(7))) * k
      }
      const s = age / 1000
      const x = x0 + vx * (1 - Math.exp(-DRAG_X * s)) / DRAG_X
      const y = y0 + 20 * k * s + (vy - 20 * k) * (1 - Math.exp(-DRAG_Y * s)) / DRAG_Y
      const u = age / p.life
      const r = p.size * k * (0.5 + 1.2 * u)
      const al = Math.min(255, 110 * (1 - u) ** 1.5 * fadeAll * DUST_GAIN)
      lc.fillStyle = `rgba(${DUST_RGB},${(al / 255).toFixed(3)})`
      lc.beginPath(); lc.ellipse(x, y, r, r * 0.6, 0, 0, Math.PI * 2); lc.fill()
    })
  })
}
